import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PortalDataError, PortalLoadingState } from "./PortalDataState";
import { useClaimsVolumeTrendQuery, useTop5InputsQuery } from "@/lib/portal-queries";
import { buildHistoricalFailureModeRows } from "@/lib/historical-failure-modes";
import { ChartDataTable } from "./ChartDataTable";

export function HistoricalFailureModes({ className = "" }: { className?: string }) {
  const top5 = useTop5InputsQuery();
  const claimsVolume = useClaimsVolumeTrendQuery();

  if (top5.isPending || claimsVolume.isPending) {
    return (
      <section className={`rounded-xl border border-border bg-card p-5 ${className}`}>
        <PortalLoadingState label="Loading historical failure modes" />
      </section>
    );
  }
  if (top5.isError || claimsVolume.isError) {
    return (
      <section className={`rounded-xl border border-border bg-card p-5 ${className}`}>
        <PortalDataError
          error={top5.error ?? claimsVolume.error}
          onRetry={() => {
            void top5.refetch();
            void claimsVolume.refetch();
          }}
        />
      </section>
    );
  }

  const rows = buildHistoricalFailureModeRows(top5.data, claimsVolume.data);
  const failureWidget = top5.data.widgets.find(
    ({ payload }) => payload.category === "failure_modes",
  );
  const k002 = claimsVolume.data.series.find(({ metricId }) => metricId === "K002");

  return (
    <section
      className={`rounded-xl border border-border p-5 ${className}`}
      style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Historical Failure Modes</h2>
          <p className="text-xs text-muted-foreground">
            Real imported failure descriptions ranked by claim count
          </p>
        </div>
        <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-1 text-[10px] font-semibold text-emerald-300">
          HISTORICAL REAL DATA
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="mt-5 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No governed historical failure-mode ranking is available. Demonstration values were not
          substituted.
        </div>
      ) : (
        <>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={rows}
                layout="vertical"
                margin={{ top: 8, right: 18, bottom: 8, left: 8 }}
              >
                <CartesianGrid
                  stroke="var(--color-border)"
                  strokeDasharray="3 3"
                  horizontal={false}
                />
                <XAxis
                  xAxisId="claims"
                  type="number"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <XAxis xAxisId="percent" type="number" domain={[0, 100]} hide />
                <YAxis
                  dataKey="shortName"
                  type="category"
                  width={142}
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(value, name) =>
                    name === "Cumulative share"
                      ? [`${Number(value).toFixed(1)}%`, name]
                      : [Number(value).toLocaleString(), name]
                  }
                  labelFormatter={(_, payload) => payload[0]?.payload?.name ?? ""}
                  contentStyle={{
                    background: "var(--color-popover)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar
                  xAxisId="claims"
                  dataKey="claimCount"
                  name="Claims"
                  fill="var(--color-primary)"
                  radius={[0, 4, 4, 0]}
                />
                <Line
                  xAxisId="percent"
                  dataKey="cumulativePercent"
                  name="Cumulative share"
                  stroke="var(--color-warning)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "var(--color-warning)" }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
            <span className="rounded border border-border px-2 py-1">
              {k002?.total?.toLocaleString()} imported claims
            </span>
            <span className="rounded border border-border px-2 py-1">
              {failureWidget?.freshness ?? "UNKNOWN"} · {failureWidget?.quality ?? "INSUFFICIENT"} ·{" "}
              {failureWidget?.reconciliation ?? "UNKNOWN"}
            </span>
            {k002?.dataCutoffAt && (
              <span className="rounded border border-border px-2 py-1">
                Data through {new Date(k002.dataCutoffAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="mt-3 text-[11px] leading-5 text-amber-300">
            Useful for historical pattern discovery; not a view of current operations. The line is
            cumulative share of all imported claims.
          </p>
          <ChartDataTable
            chartTitle="Historical Failure Modes"
            columns={[
              { key: "name", label: "Failure mode" },
              { key: "claimCount", label: "Claims", unit: "count", numeric: true },
              {
                key: "cumulativePercent",
                label: "Cumulative share",
                unit: "percent",
                numeric: true,
              },
            ]}
            rows={rows.map((row) => ({
              name: row.name,
              claimCount: row.claimCount,
              cumulativePercent: `${row.cumulativePercent.toFixed(1)}%`,
            }))}
            summary={`${rows.length} ranked failure modes across ${k002?.total?.toLocaleString() ?? "an unknown number of"} imported claims.`}
            contextNote={`HISTORICAL REAL DATA · ${failureWidget?.sourceSummary ?? "Historical import"} · ${failureWidget?.freshness ?? "UNKNOWN"} freshness · ${failureWidget?.quality ?? "INSUFFICIENT"} quality · ${failureWidget?.reconciliation ?? "UNKNOWN"} reconciliation.${k002?.dataCutoffAt ? ` Data through ${new Date(k002.dataCutoffAt).toLocaleDateString()}.` : ""} Useful for historical pattern discovery; not a view of current operations.`}
          />
        </>
      )}
    </section>
  );
}
